import React, { useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Wrench } from 'lucide-react';
import { Button } from './ui/button';

// Scroll to top on route change
export function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

// Reveal-on-scroll wrapper
export function Reveal({ children, className = '', delay = 0, as: Tag = 'div' }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.style.transitionDelay = `${delay}ms`;
            e.target.classList.add('is-visible');
            obs.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [delay]);
  return (
    <Tag ref={ref} className={`reveal ${className}`}>{children}</Tag>
  );
}

// Section eyebrow + heading
export function SectionHeading({ eyebrow, title, sub, center = false, light = false }) {
  return (
    <div className={`${center ? 'text-center mx-auto' : ''} max-w-2xl`}>
      {eyebrow && (
        <span className="inline-block text-brand-clay text-xs font-semibold uppercase tracking-[0.25em] mb-3">{eyebrow}</span>
      )}
      <h2 className={`font-display text-3xl md:text-[42px] leading-[1.1] font-semibold text-balance ${light ? 'text-brand-ivory' : 'text-brand-charcoal'}`}>{title}</h2>
      {sub && <p className={`mt-4 text-[17px] leading-relaxed ${light ? 'text-brand-sand/80' : 'text-brand-stone'}`}>{sub}</p>}
    </div>
  );
}

// Reusable estimate call-to-action banner
export function EstimateBanner() {
  return (
    <section className="bg-brand-forest">
      <div className="max-w-7xl mx-auto px-6 py-16 md:py-20 flex flex-col md:flex-row items-center justify-between gap-8 text-center md:text-left">
        <div className="max-w-2xl">
          <span className="text-brand-sand/70 text-xs font-semibold uppercase tracking-[0.25em]">Free · No Obligation</span>
          <h2 className="font-display text-3xl md:text-[40px] font-semibold text-brand-ivory mt-3 leading-tight">Let’s transform your space from the floor up.</h2>
          <p className="text-brand-sand/80 mt-3 text-[17px]">Book a free in-home consultation — we’ll bring the showroom to you.</p>
        </div>
        <Button asChild className="bg-brand-clay hover:bg-brand-clayDark text-white rounded-full px-8 h-14 text-base font-medium shadow-lg shrink-0">
          <Link to="/estimate">Request Your Estimate</Link>
        </Button>
      </div>
    </section>
  );
}

// Sister-brand promo bar: Monti Home Pros
export function HomeServicesBar() {
  return (
    <section className="bg-brand-charcoal">
      <div className="max-w-7xl mx-auto px-6 py-6 flex flex-col sm:flex-row items-center justify-center sm:justify-between gap-4 text-center sm:text-left">
        <div className="flex items-center gap-3">
          <span className="w-11 h-11 rounded-xl bg-brand-clay/20 text-brand-clay flex items-center justify-center shrink-0">
            <Wrench size={20} />
          </span>
          <p className="text-brand-ivory text-[17px] font-medium leading-snug">
            Need more than floors? <span className="text-brand-clay font-semibold">Monti Home Pros</span> handles repairs, painting, remodeling &amp; more
          </p>
        </div>
        <Button asChild variant="outline" className="rounded-full px-6 h-11 bg-transparent border-brand-sand/40 text-brand-ivory hover:bg-brand-clay hover:border-brand-clay hover:text-white font-medium shrink-0">
          <Link to="/">Explore Home Pros</Link>
        </Button>
      </div>
    </section>
  );
}

